import { Injectable } from '@angular/core';

export interface AppliedFilter {
  name: string;
  values: number[] | string[];
}

export type FilterFieldAliases = Record<string, string>;

export const WHITELISTED_WALLET_FILTER_ALIASES: FilterFieldAliases = {
  status: 'walletStatuses',
  blockchain: 'networkTypes',
  'risk level': 'riskLevels',
  'withdrawal type': 'whitelistedWalletTypes',
};

@Injectable({ providedIn: 'root' })
export class FilterService {
  applySelectedFilters(selected: AppliedFilter[], filterMap: any, aliases: FilterFieldAliases = {}): any {
    const normalizedAliases = this.normalizeAliases(aliases);

    for (const { name, values } of selected) {
      const normalizedName = name.toLowerCase();
      const aliasedField = normalizedAliases[normalizedName];

      if (aliasedField) {
        filterMap[aliasedField] = values;
        continue;
      }

      switch (normalizedName) {
        case 'transaction type':
          filterMap.transactionType = values;
          break;
        case 'trade type':
          filterMap.tradeType = values;
          break;
        case 'status':
          filterMap.status = values;
          break;
        case 'blockchain':
          filterMap.blockchain = values;
          break;
        case 'risk level':
          filterMap.riskLevel = values;
          break;
        case 'exchangecategories':
          filterMap.exchangeCategories = values.map(Number);
          break;
        case 'activity type':
          filterMap.activityType = values;
          break;
        case 'action required':
          filterMap.actionRequired = values.map((v) => Number(v) === 1);
          break;
        case 'exchange rate':
          filterMap.exchangeRateOverridden = values.length === 1 ? Number(values[0]) === 1 : undefined;
          break;
        case 'merchantids':
          filterMap.merchantIds = values;
          break;
        case 'sweepdisabledreasons':
          filterMap.sweepDisabledReasons = values;
          break;
        case 'wallet type':
          filterMap.walletType = values;
          break;
        case 'pool wallet type':
          filterMap.systemWalletType = values;
          break;
        case 'lifecycle state':
          filterMap.lifecycleState = values;
          break;
        case 'balance':
          filterMap.balance = values;
          break;
        case 'withdrawal type':
          filterMap.whitelistedWalletTypes = values;
          break;
        case 'task type':
          filterMap.type = values;
          break;
        case 'preparedby':
          filterMap.preparedBy = values;
          break;
        case 'resolvedby':
          filterMap.resolvedBy = values;
          break;
        case 'relatedmerchantid':
          filterMap.relatedMerchantId = values;
          break;
        case 'prepareddate':
          filterMap.createdAtFrom = values[0];
          filterMap.createdAtTo = values[1];
          break;
        case 'resolveddate':
          filterMap.resolvedAtFrom = values[0];
          filterMap.resolvedAtTo = values[1];
          break;
      }
    }

    return filterMap;
  }

  getAppliedFilters(filterMap: any, aliases: FilterFieldAliases = {}): AppliedFilter[] {
    const normalizedAliases = this.normalizeAliases(aliases);
    const out: AppliedFilter[] = [];
    const aliasedFields = new Set(Object.values(normalizedAliases));

    for (const [filterName, fieldName] of Object.entries(normalizedAliases)) {
      const val = filterMap[fieldName];
      if (!val && val !== false) continue;

      out.push({ name: this.toDisplayName(filterName), values: val });
    }

    for (const key of Object.keys(filterMap)) {
      if (aliasedFields.has(key)) continue;

      const val = filterMap[key];
      if (!val && val !== false) continue;

      switch (key) {
        case 'transactionType':
          out.push({ name: 'Transaction Type', values: val });
          break;
        case 'tradeType':
          out.push({ name: 'Trade Type', values: val });
          break;
        case 'status':
          out.push({ name: 'Status', values: val });
          break;
        case 'blockchain':
          out.push({ name: 'Blockchain', values: val });
          break;
        case 'networkTypes':
          out.push({ name: 'Blockchain', values: val });
          break;
        case 'riskLevel':
          out.push({ name: 'Risk Level', values: val });
          break;
        case 'exchangeCategories':
          out.push({ name: 'exchangeCategories', values: val });
          break;
        case 'activityType':
          out.push({ name: 'Activity Type', values: val });
          break;
        case 'actionRequired':
          out.push({
            name: 'Action Required',
            values: (Array.isArray(val) ? val : [val]).map((v: boolean) => (v ? 1 : 0)),
          });
          break;
        case 'exchangeRateOverridden':
          out.push({ name: 'Exchange Rate', values: [val ? 1 : 0] });
          break;
        case 'merchantIds':
          out.push({ name: 'merchantIds', values: val });
          break;
        case 'sweepDisabledReasons':
          out.push({ name: 'sweepDisabledReasons', values: val });
          break;
        case 'walletType':
          out.push({ name: 'Wallet Type', values: val });
          break;
        case 'systemWalletType':
          out.push({ name: 'Pool Wallet Type', values: val });
          break;
        case 'lifecycleState':
          out.push({ name: 'Lifecycle State', values: val });
          break;
        case 'balance':
          out.push({ name: 'Balance', values: val });
          break;
        case 'type':
          out.push({ name: 'Task Type', values: val });
          break;
        case 'preparedBy':
          out.push({ name: 'preparedBy', values: val });
          break;
        case 'resolvedBy':
          out.push({ name: 'resolvedBy', values: val });
          break;
        case 'relatedMerchantId':
          out.push({ name: 'relatedMerchantId', values: val });
          break;
        case 'createdAtFrom':
        case 'createdAtTo':
          if (filterMap.createdAtFrom && filterMap.createdAtTo)
            out.push({ name: 'preparedDate', values: [filterMap.createdAtFrom, filterMap.createdAtTo] });
          break;
        case 'resolvedAtFrom':
        case 'resolvedAtTo':
          if (filterMap.resolvedAtFrom && filterMap.resolvedAtTo)
            out.push({ name: 'resolvedDate', values: [filterMap.resolvedAtFrom, filterMap.resolvedAtTo] });
          break;
      }
    }

    return out;
  }

  private toDisplayName(name: string): string {
    return name
      .split(' ')
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  private normalizeAliases(aliases: FilterFieldAliases): FilterFieldAliases {
    return Object.fromEntries(Object.entries(aliases).map(([key, value]) => [key.trim().toLowerCase(), value]));
  }
}
